import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, MoreHorizontal, Pencil, Trash2, Package, Cylinder, Flame, Wrench, Box, Boxes, ArrowLeft, Check, X } from 'lucide-react';
import { useProductTypes } from '@/hooks/useProductTypes';
import { ProductTypeDialog } from '@/components/Products/ProductTypeDialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Link } from 'react-router-dom';
import type { ProductType, ProductTypeFormData } from '@/types/productType';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Package,
  Cylinder,
  Flame,
  Wrench,
  Box,
  Boxes,
};

const ProductTypesPage = () => {
  const {
    productTypes,
    isLoading,
    createProductType,
    updateProductType,
    deleteProductType,
  } = useProductTypes();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<ProductType | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState<ProductType | null>(null);

  const handleCreate = () => {
    setSelectedType(null);
    setDialogOpen(true);
  };

  const handleEdit = (productType: ProductType) => {
    setSelectedType(productType);
    setDialogOpen(true);
  };

  const handleDelete = (productType: ProductType) => {
    setTypeToDelete(productType);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (typeToDelete) {
      await deleteProductType(typeToDelete.id);
      setDeleteDialogOpen(false);
      setTypeToDelete(null);
    }
  };

  const handleSave = async (data: ProductTypeFormData): Promise<boolean> => {
    if (selectedType) {
      return await updateProductType(selectedType.id, data);
    } else {
      const result = await createProductType(data);
      return result !== null;
    }
  };

  const getIcon = (iconName?: string) => {
    const Icon = iconMap[iconName || 'Package'] || Package;
    return <Icon className="h-4 w-4" />;
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/dashboard/products">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Tipos de Produto</h1>
          <p className="text-muted-foreground">
            Configure os tipos de produto disponíveis para sua empresa
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Novo Tipo
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Tipos Cadastrados</CardTitle>
          <CardDescription>
            Cada tipo define se produtos podem ser locados, vendidos, e se requerem rastreamento serial
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : productTypes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum tipo de produto cadastrado</p>
              <Button variant="outline" className="mt-4" onClick={handleCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Criar primeiro tipo
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead className="text-center">Venda</TableHead>
                  <TableHead className="text-center">Locação</TableHead>
                  <TableHead className="text-center">Rastreamento</TableHead>
                  <TableHead className="text-center">Variação</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {productTypes.map((type) => (
                  <TableRow key={type.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getIcon(type.icon)}
                        <span className="font-medium">{type.name}</span>
                      </div>
                      {type.description && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {type.description}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {type.allows_sale ? (
                        <Check className="h-4 w-4 text-green-600 mx-auto" />
                      ) : (
                        <X className="h-4 w-4 text-muted-foreground mx-auto" />
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {type.allows_rental ? (
                        <Check className="h-4 w-4 text-green-600 mx-auto" />
                      ) : (
                        <X className="h-4 w-4 text-muted-foreground mx-auto" />
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {type.requires_serial_tracking ? (
                        <Check className="h-4 w-4 text-green-600 mx-auto" />
                      ) : (
                        <X className="h-4 w-4 text-muted-foreground mx-auto" />
                      )}
                    </TableCell>
                    {/* GRADE-UX parte 3, ajustes (I.178, D6): "Permite variação" */}
                    <TableCell className="text-center">
                      {type.allows_variation !== false ? (
                        <Check className="h-4 w-4 text-green-600 mx-auto" />
                      ) : (
                        <X className="h-4 w-4 text-muted-foreground mx-auto" />
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(type)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDelete(type)}
                            className="text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Remover
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ProductTypeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        productType={selectedType}
        onSave={handleSave}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover tipo de produto?</AlertDialogTitle>
            <AlertDialogDescription>
              O tipo "{typeToDelete?.name}" será desativado. Produtos existentes
              deste tipo não serão afetados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground">
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductTypesPage;
